import { lazy, useEffect } from 'react'
import { redirect, useLocation, useNavigate, useParams } from 'react-router-dom'

import { ObservableQuery, OperationVariables, WatchQueryOptions } from '@apollo/client'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Features } from 'generated/featureDefinitions'
import {
  ProjectDetailsResourceHeaderDocument,
  ProjectManagementProjectStatusEnum,
  ProjectsTabsDocument,
  SaleInvoicesDetailsPageRouteDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'
import { buildModuleRoute, buildNavigation } from 'modules/core/lib/react-router'

import { contactFullName } from 'modules/finance/lib/contactsUtils'
import SaleInvoiceDetailRootPage from 'modules/finance/pages/SaleInvoiceDetailRootPage'
import { SaleInvoiceHeader } from 'modules/finance/routes/ResourceHeaders/sale_invoice_header'
import { ProjectHeader } from 'modules/project_management/components/headers/ProjectHeader'
import SaleInvoiceDetailPage from 'modules/project_management/pages/ProjectDetailsPage/tabs/SaleInvoicesPage/SaleInvoiceDetailPage'
import SaleInvoicesExporterSidePanel from 'modules/project_management/pages/ProjectDetailsPage/tabs/SaleInvoicesPage/SaleInvoicesExporterSidePanel'
import SaleInvoiceUpsertPanel from 'modules/project_management/pages/ProjectDetailsPage/tabs/SaleInvoicesPage/SaleInvoicesUpsertSidePanel'
import SaleInvoiceDetailDocumentsPage from 'modules/project_management/pages/ProjectDetailsPage/tabs/SaleInvoicesPage/SalesInvoiceDocumentPage'

import { contributorsRoutes } from './contributors'
import { jobsAndRatesRoutes } from './jobsAndRates'
import { scheduleRoutes } from './schedule'

const ProjectsRootPage = lazy(() => import('modules/project_management/pages/ProjectsRootPage'))

const ImportProjectsDetailsPage = lazy(
  () => import('modules/project_management/pages/ImportProjectsPage')
)

const ImportProjectsModalPage = lazy(
  () => import('modules/project_management/pages/ImportProjectsPage/ImportProjectsModalPage')
)

const ProjectDetailsPage = lazy(() => import('modules/project_management/pages/ProjectDetailsPage'))

const ProjectEmployeesPage = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/Employees')
)

const AssignEmployeesSingleProjectModalPage = lazy(
  () =>
    import(
      'modules/project_management/pages/ProjectDetailsPage/Employees/AssignEmployeesSingleProjectModalPage'
    )
)

const AssignSingleEmployeeModalPage = lazy(
  () =>
    import(
      'modules/project_management/pages/ProjectDetailsPage/Employees/AssignSingleEmployeeModalPage'
    )
)

const UnassignSingleEmployeeModalPage = lazy(
  () =>
    import(
      'modules/project_management/pages/ProjectDetailsPage/Employees/UnassignSingleEmployeeModalPage'
    )
)

const ExportProjectDetailsModalPage = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/ExportProjectDetailsModalPage')
)

const OverviewPage = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/tabs/OverviewPage')
)

const SettingsPage = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/SettingsPage')
)

const SpendingPage = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/tabs/SpendingPage')
)

const SubprojectsPage = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/tabs/SubprojectsPage')
)

const AddSubprojectModalPage = lazy(
  () =>
    import(
      'modules/project_management/pages/ProjectDetailsPage/tabs/SubprojectsPage/AddSubprojectModalPage'
    )
)

const DeleteSubprojectModalPage = lazy(
  () =>
    import(
      'modules/project_management/pages/ProjectDetailsPage/tabs/SubprojectsPage/DeleteSubprojectModalPage'
    )
)

const EditSubprojectModalPage = lazy(
  () =>
    import(
      'modules/project_management/pages/ProjectDetailsPage/tabs/SubprojectsPage/EditSubprojectModalPage'
    )
)

const ProjectTasksPage = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/TasksPage')
)

const TaskDetail = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/TasksPage/TaskDetail')
)

const ProjectsDefaultPage = lazy(
  () => import('modules/project_management/pages/ProjectsDefaultPage')
)
const ProjectsListPage = lazy(() => import('modules/project_management/pages/ProjectsListPage'))

const ActivateProjectModalPage = lazy(
  () => import('modules/project_management/pages/ProjectsListPage/ActivateProjectModalPage')
)

const CloseProjectModalPage = lazy(
  () => import('modules/project_management/pages/ProjectsListPage/CloseProjectModalPage')
)

const DuplicateProjectModalPage = lazy(
  () => import('modules/project_management/pages/ProjectsListPage/DuplicateProjectModalPage')
)

const ExportProjectsModalPage = lazy(
  () => import('modules/project_management/pages/ProjectsListPage/ExportProjectsModalPage')
)

const SoftDeleteProjectModalPage = lazy(
  () => import('modules/project_management/pages/ProjectsListPage/SoftDeleteProjectModalPage')
)

const ProjectsOnboardingPage = lazy(
  () => import('modules/project_management/pages/ProjectsOnboardingPage')
)

const ProjectSaleInvoicesPage = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/tabs/SaleInvoicesPage')
)

const DocumentsPage = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/tabs/DocumentsPage')
)

type enumOrigins = 'projectList' | 'projectSettings'

interface routesProps {
  projectId: string
  watchQuery: (
    options: WatchQueryOptions<OperationVariables>
  ) => ObservableQuery<OperationVariables>
  origin: enumOrigins
  includeClients: boolean
  session: { company: { id: number } }
}

interface ProjectRedirectComponentProps {
  navigation: Navigation<NavigationPaths>
}

const ProjectOverviewRedirectComponent = ({ navigation }: ProjectRedirectComponentProps) => {
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const targetPath = navigation.projects.id.details.overview.fullPath({ id: Number(id) })

    navigate(targetPath, {
      state: location.state,
      replace: true,
    })
  }, [id, location.state, navigate, navigation])

  return null
}

export const projectManagementRoutes = buildNavigation((navigation) => {
  const routeLogic = ({ projectId, watchQuery, origin, includeClients, session }: routesProps) => {
    return watchQuery({
      query: ProjectDetailsResourceHeaderDocument,
      variables: {
        projectId: Number(projectId),
        includeClient: includeClients,
        companyId: session.company.id,
        featureIds: [
          Features.projects_tasks,
          Features.projects_costs,
          Features.projects_budget,
          Features.sales_invoices_creation,
          Features.projects_overview,
        ],
      },
    }).map(({ data }) => {
      const project = data?.projectManagement.project

      if (!project) {
        return { title: '' }
      }

      const projectStatus: ProjectManagementProjectStatusEnum = project.status
      const canManageProject = project?._policies?.assignEditor.can ?? false
      const canSeeLaborCost = project?._policies?.seeLaborCost.can ?? false

      const hasProjectTasks = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
        (feature: { id: string }) => feature.id === Features.projects_tasks
      )?.available

      const hasSpending = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
        (feature: { id: string }) => feature.id === Features.projects_costs
      )?.available

      const hasProjectBudget = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
        (feature: { id: string }) => feature.id === Features.projects_budget
      )?.available

      const hasSalesInvoices = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
        (feature: { id: string }) => feature.id === Features.sales_invoices_creation
      )?.available

      const hasDocuments = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
        (feature: { id: string }) => feature.id === Features.projects_overview
      )?.available

      const canSeeRates = hasProjectBudget && hasSpending && canSeeLaborCost

      const peopleTabTitle = canSeeRates
        ? i18n.t('project_management.people_and_rates.people_tab.with_rates')
        : i18n.t('project_management.people_and_rates.people_tab.title')

      if (origin == 'projectSettings')
        return {
          title: i18n.t('project_management.meta_data.project_settings_page.title'),
          headerComponent: (
            <ResourceHeader
              title={i18n.t('project_management.meta_data.project_settings_page.title')}
            />
          ),
        }
      else
        return {
          title: i18n.t('project_management.meta_data.project_page.title', {
            project_name: project.name,
          }),
          headerComponent: (
            <ProjectHeader
              status={projectStatus}
              projectName={project.name}
              startDate={project.startDate}
              dueDate={project.dueDate}
              code={project.code}
              projectId={project.id}
              client={contactFullName(project.client)}
              canManageProject={canManageProject}
            />
          ),
          subNavigation: [
            {
              id: 'navigation.projects.id.details.overview',
              href: navigation.projects.id.details.overview.fullPath({ id: Number(project.id) }),
              title: i18n.t('project_management.overview.overview_tab'),
              index: true,
            },
            {
              id: 'navigation.projects.id.details.subprojects',
              href: navigation.projects.id.details.subprojects.fullPath({ id: Number(project.id) }),
              title: i18n.t('project_management.subprojects.subprojects_tab'),
            },
            {
              id: 'navigation.projects.id.details.tasks',
              href: navigation.projects.id.details.tasks.fullPath({ id: Number(project.id) }),
              title: i18n.t('project_management.tasks.tasks_tab'),
              variant: !hasProjectTasks ? ('upsell' as const) : ('default' as const),
            },
            {
              id: 'navigation.projects.id.details.employees',
              href: navigation.projects.id.details.employees.fullPath({ id: Number(project.id) }),
              title: peopleTabTitle,
            },
            {
              id: 'navigation.projects.id.details.spending',
              href: navigation.projects.id.details.spending.fullPath({ id: Number(project.id) }),
              title: i18n.t('project_management.spending.spending_tab'),
              isVisible: project?._policies?.assignOwner.can ?? false,
              variant: !hasSpending ? ('upsell' as const) : ('default' as const),
            },
            {
              id: 'navigation.projects.id.details.sale_invoices',
              href: navigation.projects.id.details.sale_invoices.fullPath({
                id: Number(project.id),
              }),
              title: i18n.t('project_management.sale_invoices.tab'),
              isVisible: project?._policies?.assignOwner.can ?? false,
              variant: !hasSalesInvoices ? ('upsell' as const) : ('default' as const),
            },
            {
              id: 'navigation.projects.id.details.documents',
              href: navigation.projects.id.details.documents.fullPath({ id: Number(project.id) }),
              title: i18n.t('project_management.files.tab'),
              variant: !hasDocuments ? ('upsell' as const) : ('default' as const),
            },
          ],
        }
    })
  }

  return [
    buildModuleRoute({
      path: navigation.projects.path,
      element: <ProjectsRootPage />,
      handle: {
        module: 'project_management',
        moduleName: () => i18n.t('sidebar.projects_management'),
        meta: ({ watchQuery, session }) => {
          return watchQuery({
            query: ProjectsTabsDocument,
            variables: {
              companyId: Number(session.company.id),
              featureIds: [
                Features.projects_overview,
                Features.projects_contributors,
                Features.projects_budget,
                Features.projects_costs,
              ],
            },
          }).map(({ data }) => {
            const isOwnerOrEditor = !!data?.projectManagement._policies?.projectManage.can
            const canSeeLaborCost = !!data?.projectManagement._policies?.projectSeeLaborCost.can

            const isTotalProfitabilityEnabled = !!data?.features.featuresConnection.nodes.find(
              (feature) => feature.name === 'dev_projects_total_profitability'
            )?.enabled

            const isScheduleViewEnabled =
              !!data?.companies.featureAvailabilitiesConnection.nodes.find(
                (feature) => feature.id === Features.projects_overview
              )?.available

            const isContributorsEnabled =
              !!data?.companies.featureAvailabilitiesConnection.nodes.find(
                (feature) => feature.id === Features.projects_contributors
              )?.available

            const isBudgetFeatureEnabled =
              !!data?.companies.featureAvailabilitiesConnection.nodes.find(
                (feature) => feature.id === Features.projects_budget
              )?.available

            const isCostsFeatureEnabled =
              !!data?.companies.featureAvailabilitiesConnection.nodes.find(
                (feature) => feature.id === Features.projects_costs
              )?.available

            const canSeeRates = isBudgetFeatureEnabled && isCostsFeatureEnabled && canSeeLaborCost

            if (!isOwnerOrEditor) {
              return {
                title: i18n.t('project_management.meta_data.projects_list_page.title'),
                description: i18n.t('project_management.meta_data.projects_list_page.description'),
                illustration: 'tasks_management',
              }
            }

            return {
              title: i18n.t('project_management.meta_data.projects_list_page.title'),
              description: i18n.t('project_management.meta_data.projects_list_page.description'),
              illustration: 'tasks_management',
              subNavigation: [
                {
                  id: 'navigation.projects.list.path',
                  title: i18n.t('project_management.meta_data.projects_list_page.title'),
                  href: navigation.projects.list.fullPath(),
                  index: true,
                },
                {
                  id: 'navigation.projects.schedule.path',
                  title: i18n.t('project_management.meta_data.schedule.title'),
                  variant: isScheduleViewEnabled ? ('default' as const) : ('upsell' as const),
                  href: navigation.projects.schedule.fullPath(),
                },
                {
                  id: 'navigation.projects.jobsAndRates.path',
                  title: i18n.t('project_management.meta_data.jobs_and_rates.title'),
                  href: navigation.projects.jobsAndRates.fullPath(),
                  isVisible: isTotalProfitabilityEnabled && canSeeRates,
                },
                {
                  id: 'navigation.projects.contributors.path',
                  title: i18n.t('project_management.meta_data.contributors.title'),
                  href: navigation.projects.contributors.fullPath(),
                  variant: isContributorsEnabled ? ('default' as const) : ('upsell' as const),
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <ProjectsDefaultPage />,
        },
        {
          path: navigation.projects.onboarding.path,
          element: <ProjectsOnboardingPage />,
        },
        {
          path: navigation.projects.importDetails.path,
          handle: {
            meta: () => {
              return {
                title: i18n.t('project_management.imports.title'),
              }
            },
          },
          element: <ImportProjectsDetailsPage />,
        },
        {
          path: navigation.projects.list.path,
          children: [
            {
              path: navigation.projects.list.export.path,
              element: <ExportProjectsModalPage />,
            },
            {
              path: navigation.projects.list.import.path,
              element: <ImportProjectsModalPage />,
            },
            {
              path: navigation.projects.list.projectId.path,
              children: [
                {
                  path: navigation.projects.list.projectId.duplicate.path,
                  element: <DuplicateProjectModalPage />,
                },
                {
                  path: navigation.projects.list.projectId.close.path,
                  element: <CloseProjectModalPage />,
                },
                {
                  path: navigation.projects.list.projectId.reopen.path,
                  element: <ActivateProjectModalPage />,
                },
                {
                  path: navigation.projects.list.projectId.delete.path,
                  element: <SoftDeleteProjectModalPage />,
                },
              ],
            },
          ],
          element: <ProjectsListPage />,
        },
        ...contributorsRoutes(navigation),
        ...scheduleRoutes(navigation),
        ...jobsAndRatesRoutes(navigation),
        {
          path: navigation.projects.id.path,
          handle: {
            resource: true,
            meta: ({ params, watchQuery, session }) => {
              const projectId = params['id']
              if (!projectId) return { title: '' }

              return routeLogic({
                projectId,
                watchQuery,
                origin: 'projectList',
                includeClients: true,
                session,
              })
            },
          },
          children: [
            {
              index: true,
              element: <ProjectOverviewRedirectComponent navigation={navigation} />,
            },
            {
              path: navigation.projects.id.details.path,
              children: [
                {
                  path: navigation.projects.id.details.overview.path,

                  children: [
                    {
                      path: navigation.projects.id.details.overview.export.path,
                      element: <ExportProjectDetailsModalPage />,
                    },
                  ],
                  element: <OverviewPage />,
                },
                {
                  path: navigation.projects.id.details.subprojects.path,
                  children: [
                    {
                      path: navigation.projects.id.details.subprojects.create.path,
                      element: <AddSubprojectModalPage />,
                    },
                    {
                      path: navigation.projects.id.details.subprojects.export.path,
                      element: <ExportProjectDetailsModalPage />,
                    },
                    {
                      path: navigation.projects.id.details.subprojects.subprojectId.path,
                      children: [
                        {
                          path: navigation.projects.id.details.subprojects.subprojectId.edit.path,
                          element: <EditSubprojectModalPage />,
                        },
                        {
                          path: navigation.projects.id.details.subprojects.subprojectId.delete.path,
                          element: <DeleteSubprojectModalPage />,
                        },
                      ],
                    },
                  ],
                  element: <SubprojectsPage />,
                },
                {
                  path: navigation.projects.id.details.spending.path,
                  element: <SpendingPage />,
                },
                {
                  path: navigation.projects.id.details.tasks.path,
                  children: [
                    {
                      path: navigation.projects.id.details.tasks.taskId.path,
                      element: <TaskDetail />,
                    },
                  ],
                  element: <ProjectTasksPage />,
                },
                {
                  path: navigation.projects.id.details.employees.path,
                  children: [
                    {
                      path: navigation.projects.id.details.employees.export.path,
                      element: <ExportProjectDetailsModalPage />,
                    },
                    {
                      path: navigation.projects.id.details.employees.workers.path,
                      element: <AssignEmployeesSingleProjectModalPage />,
                    },
                    {
                      path: navigation.projects.id.details.employees.unassignEmployee.path,
                      children: [
                        {
                          path: navigation.projects.id.details.employees.unassignEmployee.employeeId
                            .path,
                          children: [
                            {
                              path: navigation.projects.id.details.employees.unassignEmployee
                                .employeeId.projectWorkerId.path,
                              element: <UnassignSingleEmployeeModalPage />,
                            },
                          ],
                        },
                      ],
                    },
                    {
                      path: navigation.projects.id.details.employees.assignEmployee.path,
                      children: [
                        {
                          path: navigation.projects.id.details.employees.assignEmployee.employeeId
                            .path,
                          children: [
                            {
                              path: navigation.projects.id.details.employees.assignEmployee
                                .employeeId.projectWorkerId.path,
                              element: <AssignSingleEmployeeModalPage />,
                            },
                          ],
                        },
                      ],
                    },
                  ],
                  element: <ProjectEmployeesPage />,
                },
                {
                  path: navigation.projects.id.details.sale_invoices.path,
                  children: [
                    {
                      path: navigation.projects.id.details.sale_invoices.create.path,
                      element: <SaleInvoiceUpsertPanel />,
                    },
                    {
                      path: navigation.projects.id.details.sale_invoices.invoiceId.path,
                      element: <SaleInvoiceUpsertPanel />,
                    },
                    {
                      path: navigation.projects.id.details.sale_invoices.export.path,
                      element: <SaleInvoicesExporterSidePanel />,
                    },
                    {
                      path: navigation.projects.id.details.sale_invoices.detail.path,
                      element: <SaleInvoiceDetailRootPage />,
                      handle: {
                        resource: true,
                        meta: ({ params, watchQuery }) =>
                          watchQuery({
                            query: SaleInvoicesDetailsPageRouteDocument,
                            variables: {
                              id: Number(params['invoiceId']),
                            },
                          }).map(({ data }) => {
                            const saleInvoice = data?.finance.financialDocument
                            const saleInvoiceTitle =
                              saleInvoice && saleInvoice.documentNumber
                                ? saleInvoice.documentNumber
                                : '-'

                            return {
                              title: saleInvoiceTitle,
                              headerComponent: (
                                <SaleInvoiceHeader
                                  saleInvoice={saleInvoice}
                                  projectId={Number(params.id)}
                                />
                              ),
                              subNavigation: saleInvoice
                                ? [
                                    {
                                      id: 'overview',
                                      title: i18n.t('finance.sale_invoices.detail.overview'),
                                      href: navigation.projects.id.details.sale_invoices.detail.overview.fullPath(
                                        {
                                          id: Number(params.id),
                                          invoiceId: saleInvoice.id,
                                        }
                                      ),
                                      isVisible: true,
                                    },
                                    {
                                      id: 'documents',
                                      title: i18n.t('finance.sale_invoices.detail.documents'),
                                      href: navigation.projects.id.details.sale_invoices.detail.documents.fullPath(
                                        {
                                          id: Number(params.id),
                                          invoiceId: saleInvoice.id,
                                        }
                                      ),
                                      isVisible: true,
                                    },
                                  ]
                                : undefined,
                            }
                          }),
                      },
                      children: [
                        {
                          index: true,
                          loader: ({ params }) => {
                            return redirect(
                              navigation.projects.id.details.sale_invoices.detail.overview.fullPath(
                                {
                                  id: Number(params.id),
                                  invoiceId: Number(params.invoiceId),
                                }
                              )
                            )
                          },
                        },
                        {
                          path: navigation.projects.id.details.sale_invoices.detail.overview.path,
                          element: <SaleInvoiceDetailPage />,
                        },
                        {
                          path: navigation.projects.id.details.sale_invoices.detail.documents.path,
                          element: <SaleInvoiceDetailDocumentsPage />,
                        },
                        {
                          path: navigation.finance.sales.salesInvoices.detail.form.path,
                          element: <SaleInvoiceUpsertPanel />,
                        },
                      ],
                    },
                  ],
                  element: <ProjectSaleInvoicesPage />,
                },
                {
                  path: navigation.projects.id.details.documents.path,
                  element: <DocumentsPage />,
                },
              ],
              element: <ProjectDetailsPage />,
            },
            {
              path: navigation.projects.id.settings.path,
              handle: {
                resource: true,
                meta: ({ params, watchQuery, session }) => {
                  const projectId = params['id']
                  if (!projectId) return { title: '' }

                  return routeLogic({
                    projectId,
                    watchQuery,
                    origin: 'projectSettings',
                    includeClients: false,
                    session,
                  })
                },
              },
              element: <SettingsPage />,
            },
          ],
        },
      ],
    }),
  ]
})
